<template>
    <div class="recipients-input">
        <div class="recipient-entry" :class="{ 'is-invalid': error }">
            <input
                v-model.trim="draft"
                :type="type === 'email' ? 'email' : 'tel'"
                :inputmode="type === 'email' ? 'email' : 'tel'"
                :placeholder="placeholder"
                :aria-label="placeholder"
                @input="error = ''"
                @keydown.enter.prevent="addRecipient"
            />
            <button type="button" :disabled="!draft" @click="addRecipient">
                <i class="fa-solid fa-plus"></i>
                {{ __('notification_management.add_recipient') }}
            </button>
        </div>

        <small v-if="error" class="recipient-error">{{ error }}</small>

        <div v-if="recipients.length" class="recipient-pills">
            <span v-for="recipient in recipients" :key="recipient" class="recipient-pill">
                <i :class="type === 'email' ? 'fa-solid fa-envelope' : 'fa-solid fa-phone'"></i>
                <span>{{ recipient }}</span>
                <button
                    type="button"
                    :title="__('notification_management.remove_recipient')"
                    :aria-label="`${__('notification_management.remove_recipient')}: ${recipient}`"
                    @click="removeRecipient(recipient)"
                >
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NotificationRecipientsInput',
    props: {
        recipients: {
            type: Array,
            required: true
        },
        type: {
            type: String,
            required: true,
            validator: (value) => ['email', 'phone'].includes(value)
        },
        placeholder: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            draft: '',
            error: ''
        }
    },
    methods: {
        addRecipient() {
            const recipient = this.draft.trim()
            if (!recipient) return

            if (!this.isValid(recipient)) {
                this.error = this.__(
                    this.type === 'email'
                        ? 'notification_management.invalid_email_recipient'
                        : 'notification_management.invalid_phone_recipient'
                )
                return
            }

            const alreadyExists = this.recipients.some(
                (item) => item.toLowerCase() === recipient.toLowerCase()
            )
            if (alreadyExists) {
                this.error = this.__('notification_management.duplicate_recipient')
                return
            }

            this.$emit('input', [...this.recipients, recipient])
            this.draft = ''
            this.error = ''
        },
        removeRecipient(recipient) {
            this.$emit(
                'input',
                this.recipients.filter((item) => item !== recipient)
            )
        },
        isValid(value) {
            if (this.type === 'email') {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            }

            return /^\+?[0-9\s().-]{6,20}$/.test(value) && /[0-9]/.test(value)
        }
    }
}
</script>

<style scoped lang="scss">
.recipients-input {
    margin-top: 12px;
}

.recipient-entry {
    display: flex;
    min-height: 42px;
    overflow: hidden;
    border: 1px solid #d2d2d2;
    border-radius: 12px;
    background: #fff;

    &:focus-within {
        border-color: var(--dark-blue);
        box-shadow: 0 0 0 3px rgba(29, 95, 159, 0.12);
    }

    &.is-invalid {
        border-color: var(--danger);
    }

    input {
        min-width: 0;
        flex: 1;
        border: 0;
        padding: 8px 12px;
        background: transparent;
        color: #333;
        outline: 0;
        font-size: 13px;
    }

    > button {
        display: flex;
        align-items: center;
        gap: 6px;
        border: 0;
        border-left: 1px solid #d2d2d2;
        padding: 0 14px;
        background: var(--dark-blue);
        color: #fff;
        font-family: 'Stolzl-Medium';
        font-size: 11px;

        &:disabled {
            background: #aab3bc;
            cursor: not-allowed;
        }
    }
}

.recipient-error {
    display: block;
    margin-top: 6px;
    color: var(--danger);
    font-size: 10px;
}

.recipient-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-top: 10px;
}

.recipient-pill {
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    gap: 7px;
    border: 1px solid rgba(29, 95, 159, 0.2);
    border-radius: 999px;
    padding: 5px 6px 5px 10px;
    background: rgba(29, 95, 159, 0.08);
    color: var(--dark-blue);
    font-size: 11px;

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    button {
        display: inline-flex;
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 50%;
        padding: 0;
        background: var(--dark-blue);
        color: #fff;
        font-size: 10px;
    }
}

@media (max-width: 520px) {
    .recipient-entry {
        flex-direction: column;

        > button {
            min-height: 38px;
            justify-content: center;
            border-top: 1px solid #d2d2d2;
            border-left: 0;
        }
    }
}
</style>
